<template>
  <div class="user-group-create-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-plus"></i>
          <span>{{ $t('userManagement.createGroup') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 用户分组创建表单 -->
    <el-card class="user-group-create-form" shadow="hover">
      <el-form
        :model="groupForm"
        :rules="groupFormRules"
        ref="groupForm"
        label-width="120px"
      >
        <!-- 分组基本信息 -->
        <el-form-item :label="$t('userManagement.groupName')" prop="name">
          <el-input
            v-model="groupForm.name"
            :placeholder="$t('userManagement.enterGroupName')"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('userManagement.description')" prop="description">
          <el-input
            v-model="groupForm.description"
            type="textarea"
            rows="4"
            :placeholder="$t('userManagement.enterGroupDescription')"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>

        <!-- 用户分配 -->
        <el-form-item :label="$t('userManagement.assignedUsers')" prop="user_ids">
          <div class="user-selection-wrapper">
            <el-select
              v-model="groupForm.user_ids"
              multiple
              filterable
              remote
              reserve-keyword
              :placeholder="$t('userManagement.selectUsersToAssign')"
              :remote-method="remoteSearchUsers"
              :loading="loadingUsers"
              style="width: 100%; flex: 1;"
            >
              <el-option
                v-for="user in remoteUsers"
                :key="user.userid"
                :label="user.username"
                :value="user.userid"
              >
                <span class="username">{{ user.username }}</span>
                <span class="email">({{ user.email }})</span>
              </el-option>
            </el-select>
            <el-button 
              type="primary" 
              @click="handleAddUsers" 
              class="add-user-btn"
              v-if="hasAddUserPermission"
            >
              <i class="el-icon-plus"></i> {{ $t('userManagement.addUsers') }}
            </el-button>
          </div>
          <div class="user-selection-hint">{{ $t('userManagement.searchUserHint') }}</div>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <el-button type="primary" @click="handleSubmit" v-if="hasPermission('sys:usergroup:save')">
            {{ $t('common.submit') }}
          </el-button>
          <el-button @click="handleResetForm">
            {{ $t('common.reset') }}
          </el-button>
          <el-button @click="handleBack">
            {{ $t('common.cancel') }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 添加用户对话框 -->
    <el-dialog
      :title="$t('userManagement.addUsersToGroup')"
      :visible.sync="addUsersDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      append-to-body
    >
      <user-group-add-users
        v-if="addUsersDialogVisible"
        :exclude-user-ids="groupForm.user_ids"
        @success="handleAddUsersSuccess"
        @close="addUsersDialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getUserList } from '@/apis/user/user'
import UserGroupAddUsers from './UserGroupAddUsers.vue'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'UserGroupCreate',
  components: {
    UserGroupAddUsers
  },
  data() {
    return {
      groupForm: {
        name: '',
        description: '',
        user_ids: []
      },
      groupFormRules: {
        name: [
          { required: true, message: this.$t('userManagement.groupNameRequired'), trigger: 'blur' },
          { min: 2, max: 50, message: this.$t('userManagement.groupNameLength'), trigger: 'blur' }
        ],
        description: [
          { max: 200, message: this.$t('userManagement.groupDescriptionLength'), trigger: 'blur' }
        ],
        user_ids: [
          { type: 'array', required: false, trigger: 'change' }
        ]
      },
      loadingUsers: false,
      remoteUsers: [],
      addUsersDialogVisible: false
    }
  },
  computed: {
    hasAddUserPermission() {
      return this.hasPermission('sys:usergroup:save')
    }
  },
  methods: {
    hasPermission,
    // 返回上一页
    handleBack() {
      this.$router.back()
    },
    
    // 重置表单
    handleResetForm() {
      this.$refs.groupForm.resetFields()
      this.remoteUsers = []
    },
    
    // 远程搜索用户
    async remoteSearchUsers(query) {
      if (query !== '') {
        this.loadingUsers = true
        try {
          const res = await getUserList({ username: query, page: 1, limit: 20 })
          // 这里的 res 已经是 response.data 了
          if (res) {
            const rawUsers = res.list || res.records || (Array.isArray(res) ? res : [])
            const newUsers = rawUsers.map(u => ({
              ...u,
              userid: u.userId || u.userid || u.id || ''
            }))
            
            // 合并并去重，保留已选中的用户
            const selectedUsers = this.remoteUsers.filter(u => this.groupForm.user_ids.includes(u.userid))
            const combined = [...selectedUsers, ...newUsers]
            const uniqueMap = new Map()
            combined.forEach(u => uniqueMap.set(String(u.userid), u))
            this.remoteUsers = Array.from(uniqueMap.values())
          }
        } catch (error) {
          console.error('Search users failed:', error)
        } finally {
          this.loadingUsers = false
        }
      }
    },

    // 打开添加用户对话框
    handleAddUsers() {
      this.addUsersDialogVisible = true
    },

    // 添加用户成功回调
    handleAddUsersSuccess(data) {
      if (data.success && data.selectedUsers) {
        // 获取新选择的用户 ID
        const newUserIds = data.selectedUsers.map(u => String(u.userid))
        
        // 更新表单中的已选用户 ID（去重）
        const combinedIds = [...this.groupForm.user_ids.map(id => String(id)), ...newUserIds]
        this.groupForm.user_ids = [...new Set(combinedIds)]
        
        // 同时更新 remoteUsers 列表，确保 el-select 能显示这些用户的 label
        const combinedUsers = [...this.remoteUsers, ...data.selectedUsers]
        const uniqueMap = new Map()
        combinedUsers.forEach(u => uniqueMap.set(String(u.userid), u))
        this.remoteUsers = Array.from(uniqueMap.values())
        
        this.addUsersDialogVisible = false
        this.$message.success(this.$t('userManagement.addUsersSuccess'))
      }
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.groupForm.validate((valid) => {
        if (valid) {
          // 创建分组
          this.$store.dispatch('createUserGroup', {
            name: this.groupForm.name,
            description: this.groupForm.description,
            user_ids: this.groupForm.user_ids
          }).then(() => {
            this.$message.success(this.$t('userManagement.groupCreateSuccess'))
            this.handleBack()
          }).catch(err => {
            this.$message.error(this.$t('userManagement.groupCreateError'))
          })
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped>
.user-group-create-container {
  padding: 0;
}

.control-panel {
  margin-bottom: 24px;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
  
  i {
    font-size: 18px;
    color: #409eff;
  }
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-group-create-form {
  max-width: 800px;
}

.user-selection-wrapper {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.add-user-btn {
  white-space: nowrap;
}

.user-selection-hint {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
  line-height: 1;
}

.username {
  font-weight: 500;
}

.email {
  color: #606266;
  margin-left: 10px;
}
</style>